import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { endOfDay } from 'date-fns'
import { WritableDraft } from 'immer'

import {
  REFERRAL_IMPORT_DIRECTION,
  REFERRAL_STATUS_CODE,
  SERVICE_AREA_ID,
} from '../../lib/constants'
import { ClientDetailsInput } from '../../pages/referClient/steps/4_ClientDetails/constants'
import { HydraResource } from '../../types/api'
import { ImportDirection } from '../../types/dtos/bulk-import-referral-details'
import { CompanyEntity, UserEntity } from '../../types/entities'
import { FormErrors } from '../../types/misc'
import { ClientItem } from '../../types/responses/clients'
import { SessionMetadataCompany } from '../../types/responses/session-metadata'
import { RootState } from '../store'

export interface LogHistoricalReferralState {
  isOpen: boolean
  isUserValid: boolean | null
  isClientValid: boolean | null
  isNewClientFormValid: boolean | null
  isCompanyAdvisorValid: boolean | null
  isServiceAreaValid: boolean | null
  isStatusValid: boolean | null
  isValid: boolean | null
  logStatus: 'invalid' | 'ready' | 'processing' | 'imported' | 'error'
  logErrors?: FormErrors
  logData: LogHistoricalReferralData
  userCompany: SessionMetadataCompany | null
  userCompanyUser: HydraResource<
    Pick<UserEntity, 'id' | 'firstName' | 'lastName' | 'email'>
  > | null
}

export interface LogHistoricalReferralData {
  circumstances: string
  otherCompany: Pick<CompanyEntity, 'id' | 'presentationName'> | null
  otherCompanyUser: Pick<
    UserEntity,
    'id' | 'firstName' | 'lastName' | 'email'
  > | null
  otherCompanyUserDefaultUsed: boolean
  inviteAdvisor: boolean
  advisorEmail: string
  advisorEmailValid: boolean
  existingClient: ClientItem | null
  newClientDateOfBirth: string
  newClientEmail: string
  newClientEmailValid: boolean
  newClientFirstName: string
  newClientLastName: string
  newClientTelephone: string
  dateReferred: string
  serviceAreaId: number
  referralStatus: string
  referralDirection: ImportDirection
}

const endOfToday = endOfDay(new Date())

const INITIAL_STATE: LogHistoricalReferralState = {
  isOpen: false,
  isUserValid: null,
  isClientValid: null,
  isNewClientFormValid: null,
  isCompanyAdvisorValid: null,
  isServiceAreaValid: null,
  isStatusValid: null,
  isValid: null,
  logStatus: 'invalid',
  logErrors: undefined,
  logData: {
    circumstances: '',
    otherCompany: null,
    otherCompanyUser: null,
    otherCompanyUserDefaultUsed: false,
    inviteAdvisor: true,
    advisorEmail: '',
    advisorEmailValid: true,
    existingClient: null,
    newClientDateOfBirth: '',
    newClientEmail: '',
    newClientEmailValid: false,
    newClientFirstName: '',
    newClientLastName: '',
    newClientTelephone: '',
    dateReferred: endOfToday.toISOString(),
    serviceAreaId: -1,
    referralStatus: REFERRAL_STATUS_CODE.not_sure,
    referralDirection: 'TO',
  },
  userCompany: null,
  userCompanyUser: null,
}

const logHistoricalReferral = createSlice({
  name: 'logHistoricalReferral',
  initialState: INITIAL_STATE,
  reducers: {
    resetLogReferralState(state) {
      // - keep legged in user information though
      Object.assign(state, {
        ...INITIAL_STATE,
        userCompany: state.userCompany,
        userCompanyUser: state.userCompanyUser,
      })
      validateState(state)
    },

    setupUser(
      state,
      action: {
        payload: {
          userCompany: LogHistoricalReferralState['userCompany']
          userCompanyUser: LogHistoricalReferralState['userCompanyUser']
        }
      },
    ) {
      state.userCompany = action.payload.userCompany
      state.userCompanyUser = action.payload.userCompanyUser
      validateState(state)
    },

    setErrors(
      state,
      action: PayloadAction<LogHistoricalReferralState['logErrors']>,
    ) {
      state.logErrors = action.payload
      state.logStatus = 'error'
    },

    setIsNewClientFormValid(
      state,
      action: { payload: LogHistoricalReferralState['isNewClientFormValid'] },
    ) {
      state.isNewClientFormValid = action.payload
      validateState(state)
    },

    setIsOpen(
      state,
      action: { payload: LogHistoricalReferralState['isOpen'] },
    ) {
      state.isOpen = action.payload
    },

    setNewClientData(state, action: PayloadAction<ClientDetailsInput>) {
      const nameChunks = action.payload.fullName.split(' ')
      state.logData.newClientDateOfBirth = action.payload.dateOfBirth || ''
      state.logData.newClientEmail = action.payload.email
      state.logData.newClientEmailValid = true
      state.logData.newClientFirstName = nameChunks.shift() || ''
      state.logData.newClientLastName = nameChunks.join(' ') || ''
      state.logData.newClientTelephone = action.payload.telephone || ''
      validateState(state)
    },

    updateLogData(
      state,
      action: PayloadAction<Partial<LogHistoricalReferralData>>,
    ) {
      Object.assign(state.logData, action.payload)
      validateState(state)
    },
  },
})

function validateState(state: WritableDraft<LogHistoricalReferralState>) {
  // validate user
  state.isUserValid = !!state.userCompanyUser && !!state.userCompany

  // validate client
  if (state.logData.existingClient) {
    state.isClientValid = true
  } else {
    state.isClientValid = state.isNewClientFormValid
  }

  // validate company
  state.isCompanyAdvisorValid =
    (state.logData.advisorEmailValid && state.logData.advisorEmail !== '') ||
    !!state.logData.otherCompany

  // validate service area
  if (
    !!state.logData.advisorEmail &&
    state.logData.referralDirection === REFERRAL_IMPORT_DIRECTION.TO
  ) {
    // enforce "General Enquiry" for referring to invite
    state.logData.serviceAreaId = SERVICE_AREA_ID.generalEnquiry
  }
  state.isServiceAreaValid = state.logData.serviceAreaId > -1

  state.isStatusValid = !!state.logData.referralStatus

  state.isValid =
    state.isUserValid &&
    state.isClientValid &&
    state.isCompanyAdvisorValid &&
    state.isServiceAreaValid &&
    state.isStatusValid

  return state
}

export const logHistoricalReferralReducer = logHistoricalReferral.reducer

export const selectLogHistoricalReferralState = (state: RootState) =>
  state.logReferral

export const {
  resetLogReferralState,
  setErrors,
  setNewClientData,
  setIsNewClientFormValid,
  setIsOpen,
  setupUser,
  updateLogData,
} = logHistoricalReferral.actions
